<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, nextTick, toRef } from 'vue'
import { VIDEOS } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import MainA from './_partials/MainA.vue'
import MainB from './_partials/MainB.vue'
import stateInitializer from '@/models/stateInitializer'
import lifeCycle from '@/models/lifeCycle'
import leadGen from '@/models/leadGen'
import userCareer from '@/models/userCareer'
import customFields from '@/models/customFields'
import privacyPolicy from '@/pages/_partials/solutionContents/privacyPolicy'
import { useUserStore } from '@/stores/user'
import dynamicallyLoadScript from '@/utilities/dynamicallyLoadScript'

interface WqWindow extends Window {
  _wq: any[]
}

declare const window: WqWindow

export default defineComponent({
  name: 'VideoMain',

  components: {
    MainA,
    MainB,
  },

  emits: ['init:logoColor'],

  setup(_, { emit }) {
    // wistia の queue 初期化
    // @see https://docs.wistia.com/docs/javascript-player-api
    window._wq = window._wq || []

    const userStore = useUserStore()

    interface State {
      form: Form,
      input: {
        userInfo: {
          lastName: InputText,
          firstName: InputText,
          lastNameKana: InputText,
          firstNameKana: InputText,
          companyName: InputText,
          industry: InputText,
          scale: InputText,
          listingStatus: InputText,
          profession: InputText,
          professionDomain: InputMultiSelect,
          positionLevel: InputText,
          departmentAndPosition: InputText,
          workMailAddress: InputText,
          phoneNumber: InputText,
          corporateZipcode: InputText,
          corporateAddress1: InputText,
          corporateAddress2: InputText,
          corporateNumber: InputText,
          corporateEmployeeNum: InputNumber,
        },

        optionalNewsletterSettings: {
          mailStopFlag: InputBoolean,
          mailNewsStopFlag: InputBoolean,
          mailExpoStopFlag: InputBoolean,
          mailAiStopFlag: InputBoolean,
        },

        customFields: Inputs,

        hearingFields: Inputs,

        isFullFilled: boolean,
      },
      video: SolutionContentItem,

      /**
       * Custom Field のサーバからの情報受取用
       * ※ Custom Field の入力値格納用は State の input オブジェクトに別途用意
       */
      customFieldConfigItems?: CustomFieldConfig[],

      /**
       * ヒアリング項目のサーバからの情報受取用
       * ※ ヒアリング項目の入力値格納用は State の input オブジェクトに別途用意
       */
      hearingFieldConfig?: HearingFieldConfig,

      meta: {
        corporateBrandColor: {
          primary: string,
          secondary: string,
        },
      },

      layoutPattern: Maybe<'a' | 'b'>,

      /**
       * 音声データのみのコンテンツかどうか
       * @description CTA 周りの文言を「動画」から出し分けるために使用
       */
      isAudioOnly: boolean,
    }
    const state: State = reactive({
      form: {
        name: '',
        action: '',
        method: 'post',
      },
      input: {
        userInfo: {
          lastName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          firstName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          lastNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          firstNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          companyName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          industry: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          scale: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          listingStatus: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          profession: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          // professionDomain（担当領域）。name は view_props(videos/.../show/_main.json.jb) で
          // 'contact[profession_domain]' を設定。送信時は contacts_controller の
          // profession_domain_array_to_text で配列→テキスト変換される。
          professionDomain: {
            value: [],
            name: '',
            validationScheme: {},
          },
          positionLevel: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          departmentAndPosition: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          workMailAddress: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              email: true,
              mailDomain: true,
            },
          },
          phoneNumber: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              phoneNumber: true,
            },
          },
          corporateZipcode: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress1: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress2: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateNumber: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateEmployeeNum: {
            value: undefined,
            name: '',
            validationScheme: {},
          },
        },
        optionalNewsletterSettings: {
          mailStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailNewsStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailExpoStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
          mailAiStopFlag: {
            value: false,
            name: '',
            validationScheme: {},
          },
        },
        customFields: {},

        hearingFields: {},

        /**
         * プロフィール情報がすべて正常な値で埋まっているかどうかのサーバ側の保有値
         */
        isFullFilled: false,
      },

      video: {
        id: undefined,
        title: '',
        thumbImageUrl: '',
        introduction: '',
        solution: {
          id: undefined,
          title: '',
          categoryIds: [],
          company: {
            name: '',
          },
          privacyPolicy: '',
        },
        mediaId: '',
        isYoutube: false,
        duration: 0,
        isShowBlock: false,
      },

      customFieldConfigItems: [],

      hearingFieldConfig: {
        nameSuffix: {
          id: '',
          hearingFieldId: '',
          value: '',
          secondaryValue: '',
        },
        sections: [],
      },

      meta: {
        corporateBrandColor: {
          primary: '',
          secondary: '',
        },
      },

      layoutPattern: undefined,

      isAudioOnly: false,
    })

    stateInitializer(state, 'main')

    const { privacyPolicyCaption } = privacyPolicy(state.video.solution!)

    const firstErrorDOM = ref()
    const isShowModal = ref(false)
    const initialized = ref(false)

    const { domContentLoaded } = lifeCycle()

    const {
      isEditModeUserLeadInfo,
      isUpdateUserLeadInfo,
      selectCompanyLeadInfo,
      inputCompanyName,
      toEditModeUserLeadInfo,
      toEditModeUserLeadInfoByError,
    } = leadGen(state.input.userInfo)

    customFields(state.input.customFields, state.customFieldConfigItems)

    const { isShowProfessionDomain } = userCareer(toRef(state.input, 'userInfo'))

    /**
     * コンテンツ種別の呼称
     * @description 音声データのみのコンテンツは CTA 周りの文言を「動画」ではなく「音声」で表示する
     */
    const mediaTypeLabel = computed(() => {
      return state.isAudioOnly
        ? '音声'
        : '動画'
    })

    /**
     * submit ボタンのラベル
     */
    const submitButtonLabel = computed(() => {
      return userStore.context.isSignedIn
        ? `${mediaTypeLabel.value}を視聴する`
        : '無料登録する'
    })

    onMounted(() => {
      if (state.video.isShowBlock) {
        fetchVideoData()
      } else {
        initVideoPlay()
      }

      if (!state.input.isFullFilled) toEditModeUserLeadInfo()

      // 「担当領域」を表示する場合は、「担当領域」のバリデーションを必須にする
      if (isShowProfessionDomain.value) {
        Object.assign(
          state.input.userInfo.professionDomain,
          { validationScheme: { required: true } },
        )
      }

      // ブランドカラーが設定されている場合は BizHint ロゴをモノトーンにする
      if (state.meta.corporateBrandColor.primary && state.meta.corporateBrandColor.secondary) {
        emit('init:logoColor', 'gray')
      }
    })

    const onValidated = () => {
      if (!initialized.value) initialized.value = true
    }

    const initVideoPlay = () => {
      if (state.video.isYoutube) {
        // YouTube動画の場合は特別な初期化は不要（iframeで自動再生）
        return
      }

      /**
       * @see Wistia API https://docs.wistia.com/docs/javascript-player-api
       */
      window._wq.push({
        id: state.video.mediaId,
        options: {
          playerColor: '#000000',
        },
        onReady: function (video: any) {
          video.bind('play', () => {
            // 再生ボタンが押されたら Email を設定する
            if (import.meta.env.MODE !== 'development') {
              video.email(state.input.userInfo.workMailAddress.value)
            }
          })
        },
      })
    }

    /**
     * 動画の詳細データ取得
     * @description サムネイル画像・再生時間表示用
     */
    const fetchVideoData = async () => {
      // YouTube 動画の場合は、YouTube 用のサムネイルを設定し、API コールをスキップ
      if (state.video.isYoutube) {
        state.video.thumbImageUrl = `https://img.youtube.com/vi/${state.video.mediaId}/maxresdefault.jpg`
        return
      }

      const videoUrl = `https://home.wistia.com/medias/${state.video.mediaId!}`
      const params = {
        url: videoUrl
      }

      try {
        await apiRequestManager.handleRequest({
          endpoint: VIDEOS.WISTIA,
          params,
          callback: (response, isSuccess) => {
            if (isSuccess) {
              state.video.thumbImageUrl = response.data.thumbnail_url
              state.video.duration = response.data.duration
            }
          }
        })
      } catch (error) {
        console.log(error)
      }
    }
    // fetchVideoData()

    const loadWistiaScripts = async() => {
      // YouTube動画の場合はWistiaスクリプトを読み込まない
      if (state.video.isYoutube) {
        return
      }

      if (!state.video.isShowBlock && state.video.mediaId) {
        const WISTIA_VIDEO_SCRIPT = `https://fast.wistia.com/embed/medias/${state.video.mediaId}.jsonp`
        const WISTIA_EXTERNAL_SCRIPT = 'https://fast.wistia.com/assets/external/E-v1.js'

        await dynamicallyLoadScript(WISTIA_VIDEO_SCRIPT, { async: true })
        await dynamicallyLoadScript(WISTIA_EXTERNAL_SCRIPT, { async: true })
      }
    }

    loadWistiaScripts()

    const showModal = () => {
      isShowModal.value = true
    }

    /**
     * バリデーションエラー発生時にエラー箇所までスクロール
     *
     * @description 実際のスクロールそのものの処理はモーダルコンポーネント側が持っている
     */
    const scrollToFieldByValidationError = (): void => {
      if (!isEditModeUserLeadInfo.value) {
        toEditModeUserLeadInfoByError()
      }

      nextTick(() => {
        const _firstErrorDOM = document.querySelectorAll('[data-validation-error="true"]')[0]
        if (!_firstErrorDOM) return

        // firstErrorDOM に HTMLElement が渡った段階で、Modal コンポーネント側の watcher でスクロールイベントが発火する
        firstErrorDOM.value = _firstErrorDOM
      })
    }

    /**
     * 「担当領域」の入力が必要な「職種」が選択された場合の処理
     */
    const onSelectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: true } },
      )
    }

    /**
     * 「担当領域」の入力が不要な「職種」が選択された場合の処理
     */
    const onUnselectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: false } },
      )
    }

    /**
     * ヒアリング項目の「その他」に該当する項目が選択された際の処理
     * @description video は現状ヒアリング項目を持たない（受け口のみ）。共通レイアウト経由のイベント受け口として用意。
     */
    const onSelectInHearingMultiSelectorPrimary = (name: string, value: Input) => {
      if (state.input.hearingFields && !state.input.hearingFields[name]) {
        Object.assign(state.input.hearingFields, { [name]: value })
      }
    }
    const onCloseHearingMultiSelectorSecondary = (name: string) => {
      if (state.input.hearingFields && state.input.hearingFields[name]) {
        delete state.input.hearingFields[name]
      }
    }

    return {
      // data
      state,
      firstErrorDOM,
      isShowModal,
      isEditModeUserLeadInfo,
      domContentLoaded,
      initialized,

      // computed
      mediaTypeLabel,
      submitButtonLabel,
      isUpdateUserLeadInfo,
      privacyPolicyCaption,

      // methods
      toEditModeUserLeadInfo,
      selectCompanyLeadInfo,
      inputCompanyName,
      showModal,
      scrollToFieldByValidationError,
      onValidated,
      onSelectedProfessionOptionLinkedToDomain,
      onUnselectedProfessionOptionLinkedToDomain,
      onSelectInHearingMultiSelectorPrimary,
      onCloseHearingMultiSelectorSecondary,
    }
  },
})
</script>

<template>
  <section class="section">
    <MainA
      v-if="state.layoutPattern === 'a'"
      :state="state"
      :is-show-modal="isShowModal"
      :first-error-dom="firstErrorDOM"
      :is-edit-mode-user-lead-info="isEditModeUserLeadInfo"
      :is-update-user-lead-info="isUpdateUserLeadInfo"
      :dom-content-loaded="domContentLoaded"
      :initialized="initialized"
      :submit-button-label="submitButtonLabel"
      :media-type-label="mediaTypeLabel"
      :privacy-policy-caption="privacyPolicyCaption"
      :corporate-brand-color="state.meta.corporateBrandColor"
      @update:is-show-modal="isShowModal = $event"
      @complete:scroll="firstErrorDOM = undefined"
      @click:thumbnail="showModal"
      @validated="onValidated"
      @error="scrollToFieldByValidationError"
      @to-edit-mode="toEditModeUserLeadInfo"
      @invalid-value="toEditModeUserLeadInfo"
      @select:company-lead-info="selectCompanyLeadInfo($event)"
      @update:company-name="inputCompanyName($event)"
      @update:last-name="state.input.userInfo.lastName.value = $event"
      @update:first-name="state.input.userInfo.firstName.value = $event"
      @update:last-name-kana="state.input.userInfo.lastNameKana.value = $event"
      @update:first-name-kana="state.input.userInfo.firstNameKana.value = $event"
      @update:industry="state.input.userInfo.industry.value = $event"
      @update:scale="state.input.userInfo.scale.value = $event"
      @update:listing-status="state.input.userInfo.listingStatus.value = $event"
      @update:profession="state.input.userInfo.profession.value = $event"
      @update:position-level="state.input.userInfo.positionLevel.value = $event"
      @update:department-and-position="state.input.userInfo.departmentAndPosition.value = $event"
      @update:work-mail-address="state.input.userInfo.workMailAddress.value = $event"
      @update:phone-number="state.input.userInfo.phoneNumber.value = $event"
      @update:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
      @update:corporate-address1="state.input.userInfo.corporateAddress1.value = $event"
      @update:corporate-address2="state.input.userInfo.corporateAddress2.value = $event"
      @reset:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
      @update:custom-fields="Object.assign(state.input.customFields, $event)"
      @update:mail-stop-flag="state.input.optionalNewsletterSettings.mailStopFlag.value = $event"
      @update:mail-news-stop-flag="state.input.optionalNewsletterSettings.mailNewsStopFlag.value = $event"
      @update:mail-expo-stop-flag="state.input.optionalNewsletterSettings.mailExpoStopFlag.value = $event"
      @update:mail-ai-stop-flag="state.input.optionalNewsletterSettings.mailAiStopFlag.value = $event" />

    <MainB
      v-else-if="state.layoutPattern === 'b'"
      :state="state"
      :media-type-label="mediaTypeLabel"
      @select:company-lead-info="selectCompanyLeadInfo($event)"
      @update:company-name="inputCompanyName($event)"
      @update:last-name="state.input.userInfo.lastName.value = $event"
      @update:first-name="state.input.userInfo.firstName.value = $event"
      @update:last-name-kana="state.input.userInfo.lastNameKana.value = $event"
      @update:first-name-kana="state.input.userInfo.firstNameKana.value = $event"
      @update:industry="state.input.userInfo.industry.value = $event"
      @update:scale="state.input.userInfo.scale.value = $event"
      @update:listing-status="state.input.userInfo.listingStatus.value = $event"
      @update:profession="state.input.userInfo.profession.value = $event"
      @update:profession-domain="state.input.userInfo.professionDomain.value = $event"
      @open:profession-domain="onSelectedProfessionOptionLinkedToDomain"
      @close:profession-domain="onUnselectedProfessionOptionLinkedToDomain"
      @update:position-level="state.input.userInfo.positionLevel.value = $event"
      @update:department-and-position="state.input.userInfo.departmentAndPosition.value = $event"
      @update:work-mail-address="state.input.userInfo.workMailAddress.value = $event"
      @update:phone-number="state.input.userInfo.phoneNumber.value = $event"
      @update:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
      @update:corporate-address1="state.input.userInfo.corporateAddress1.value = $event"
      @update:corporate-address2="state.input.userInfo.corporateAddress2.value = $event"
      @reset:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event"
      @update:custom-fields="Object.assign(state.input.customFields, $event)"
      @update:hearing-fields="Object.assign(state.input.hearingFields, $event)"
      @select:hearing-multi-select-primary="onSelectInHearingMultiSelectorPrimary"
      @close:hearing-multi-select-secondary="onCloseHearingMultiSelectorSecondary($event)"
      @update:mail-stop-flag="state.input.optionalNewsletterSettings.mailStopFlag.value = $event"
      @update:mail-news-stop-flag="state.input.optionalNewsletterSettings.mailNewsStopFlag.value = $event"
      @update:mail-expo-stop-flag="state.input.optionalNewsletterSettings.mailExpoStopFlag.value = $event"
      @update:mail-ai-stop-flag="state.input.optionalNewsletterSettings.mailAiStopFlag.value = $event" />
  </section>
</template>
